/**
 * Round video — the Telegram-style clip beside an example's outcome.
 *
 * While it is on screen it loops silently in its circle (and only then does it start loading:
 * it lives far down the page, so `preload="none"` until it is near). A tap plays it from the
 * start with sound, a size bigger, with a progress ring running round the edge; a second tap,
 * or the end of the clip, puts it back to the silent loop. Under prefers-reduced-motion
 * nothing moves until it is tapped.
 */
export function roundVideo() {
  const items = document.querySelectorAll('[data-round-video]');
  if (!items.length) return;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  items.forEach((button) => {
    const video = button.querySelector('video');
    if (!video) return;
    let visible = false;
    let frame = 0;
    const isOn = () => button.getAttribute('aria-pressed') === 'true';

    const ring = () => {
      if (video.duration) button.style.setProperty('--progress', (video.currentTime / video.duration).toFixed(4));
      frame = isOn() && !video.paused ? requestAnimationFrame(ring) : 0;
    };

    const quiet = () => {
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-label', 'Pustit video se zvukem');
      button.classList.remove('is-on');
      button.style.removeProperty('--progress');
      video.muted = true;
      video.loop = true;
      if (visible && !reduceMotion) video.play().catch(() => {});
      else video.pause();
    };

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        visible = entry.isIntersecting;
        if (!visible) { video.pause(); return; }
        if (isOn() || reduceMotion) return;
        video.preload = 'auto';
        video.play().catch(() => {});
      }, { threshold: 0.4 }).observe(button);
    }

    button.addEventListener('click', () => {
      if (isOn()) { quiet(); return; }
      button.setAttribute('aria-pressed', 'true');
      button.setAttribute('aria-label', 'Ztlumit video');
      button.classList.add('is-on');
      video.loop = false;
      video.muted = false;
      video.currentTime = 0;
      video.play().catch(() => {});
      // It is usually already playing (the silent loop), so no `play` event will come.
      if (!frame) frame = requestAnimationFrame(ring);
    });
    video.addEventListener('play', () => { if (isOn() && !frame) frame = requestAnimationFrame(ring); });
    video.addEventListener('ended', quiet);
  });
}
